'use client';

import { useEffect } from 'react';

/**
 * Service Worker Registration Component
 *
 * Registers the service worker for tool bundle caching.
 * Only registers in production and when service workers are supported.
 *
 * Usage: Include this component in the root layout.
 *
 * The service worker caches tool bundles from R2 storage for faster repeat visits.
 * Since bundles are versioned (e.g., tool-name-v1.js), they are immutable and
 * can be cached aggressively with a cache-first strategy.
 */
export default function ServiceWorkerRegistration() {
  useEffect(() => {
    // Only register when service workers are supported
    if (
      typeof window === 'undefined' ||
      !('serviceWorker' in navigator)
    ) {
      return;
    }

    // Skip registration in development/test environments
    // Service workers are optional - they enhance performance but aren't required
    const isProduction = window.location.hostname === 'inventivehq.com' ||
                         window.location.hostname === 'www.inventivehq.com';
    if (!isProduction) {
      return;
    }

    // Register the service worker
    const registerServiceWorker = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/tools/',
        });

        console.log('[App] Service worker registered:', registration.scope);

        // Check for updates periodically (every hour)
        setInterval(() => {
          registration.update().catch((err) => {
            console.warn('[App] Service worker update check failed:', err);
          });
        }, 60 * 60 * 1000);

        // Handle updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (
                newWorker.state === 'installed' &&
                navigator.serviceWorker.controller
              ) {
                console.log('[App] New service worker available');
              }
            });
          }
        });
      } catch {
        // Service workers are optional - failures are expected in some environments
        // (headless browsers, test environments, incognito mode, etc.)
        // Silently ignore since this doesn't affect functionality
      }
    };

    // Register after page load to not block initial rendering
    if (document.readyState === 'complete') {
      registerServiceWorker();
    } else {
      window.addEventListener('load', registerServiceWorker);
      return () => {
        window.removeEventListener('load', registerServiceWorker);
      };
    }
  }, []);

  // This component renders nothing
  return null;
}

/**
 * Utility function to clear the tool bundle cache
 * Can be called from developer console or other components
 */
export async function clearToolBundleCache(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    console.warn('[App] No active service worker to clear cache');
    return false;
  }

  const controller = navigator.serviceWorker.controller;

  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();

    messageChannel.port1.onmessage = (event) => {
      if (event.data?.success) {
        console.log('[App] Tool bundle cache cleared successfully');
        resolve(true);
      } else {
        console.error('[App] Failed to clear cache:', event.data);
        resolve(false);
      }
    };

    controller.postMessage(
      { type: 'CLEAR_BUNDLE_CACHE' },
      [messageChannel.port2]
    );
  });
}

/**
 * Utility function to clear all PWA caches (bundles + app shell)
 * Can be called from developer console or other components
 */
export async function clearAllCaches(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    console.warn('[App] No active service worker to clear cache');
    return false;
  }

  const controller = navigator.serviceWorker.controller;

  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();

    messageChannel.port1.onmessage = (event) => {
      if (event.data?.success) {
        console.log('[App] All caches cleared successfully');
        resolve(true);
      } else {
        console.error('[App] Failed to clear caches:', event.data);
        resolve(false);
      }
    };

    controller.postMessage(
      { type: 'CLEAR_ALL_CACHES' },
      [messageChannel.port2]
    );
  });
}

/**
 * Utility function to precache a specific tool for offline use
 * Can be called from a "Save for Offline" button on tool pages
 */
export async function precacheToolForOffline(slug: string): Promise<{ success: boolean; slug: string; error?: string }> {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    return { success: false, slug, error: 'No active service worker' };
  }

  const controller = navigator.serviceWorker.controller;

  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();

    messageChannel.port1.onmessage = (event) => {
      resolve(event.data);
    };

    controller.postMessage(
      { type: 'PRECACHE_TOOL', slug },
      [messageChannel.port2]
    );
  });
}

/**
 * Utility function to get cache statistics
 * Can be called from developer console or other components
 */
export async function getToolBundleCacheStats(): Promise<{
  cacheName: string;
  bundleCount: number;
  totalSize: number;
  totalSizeFormatted: string;
  bundles: Array<{
    path: string;
    version: string | null;
    size: number;
    cachedAt: string | null;
  }>;
} | { error: string }> {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    return { error: 'No active service worker' };
  }

  const controller = navigator.serviceWorker.controller;

  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();

    messageChannel.port1.onmessage = (event) => {
      resolve(event.data);
    };

    controller.postMessage(
      { type: 'GET_CACHE_STATS' },
      [messageChannel.port2]
    );
  });
}

// Expose utilities to window for developer console access
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).clearToolBundleCache = clearToolBundleCache;
  (window as unknown as Record<string, unknown>).clearAllCaches = clearAllCaches;
  (window as unknown as Record<string, unknown>).precacheToolForOffline = precacheToolForOffline;
  (window as unknown as Record<string, unknown>).getToolBundleCacheStats = getToolBundleCacheStats;
}
